import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import LoaderWrapper from "../../../widgets/loaderWrapper/LoaderWrapper";
import MapWidget from "../../../widgets/map/mapWidget/MapWidget";
import ContentHeader from "../../../widgets/contentHeader/ContentHeader";
import ToastMessageContainer from "../../../widgets/toastMessageContainer/ToastMessageContainer";

import { getItemWebStorage } from "../../../features/webStorageSaver";
import { getAllObjectsLayer, getBasicObjectTypeLayer, getWKTFeatureVectorLayer } from "../../../widgets/map/mapFeatures/layers";
import { countExtentFromWKTFeatures } from "../../../widgets/map/mapFeatures/helpers";
import { fetchObjectGeometry } from "../../../api/mapApi";

import { useAppDispatch } from "../../../stateManagement/hooks/redux";
import { showToastMessage } from "../../../stateManagement/reducers";

import { useGetTableObjectInfoQuery } from "../../../api/rtk/base/slices/cardsApiSlice";
import { formatObject } from "../../../features/dataFormatFunctions/formatObject";
import {
    buildObjectCardHeaderTitle,
    PROPERTIES_FOR_OBJECT_MAP_HEADER_LABEL,
} from "../../../features/objectLabels/objectLabelsConfig";
import { getObjectsOnWMSLayer } from "src/widgets/map/mapFeatures/mapFunctions/getObjectsOnMapPoint/getObjectsOnMapPoint";
import { objectTypesDefaultFilters } from "../mapObjectType/MapObjectType";

type ObjectTypeMeta = {
    name?: string;
    queryType?: string;
    captionSingular?: string;
    mapLayer?: string;
};

export function MapObject() {
    const { objectId, objectTypeName } = useParams();
    const dispatch = useAppDispatch();

    const currentConfig = getItemWebStorage("currentConfig") ?? {};
    const { wmsServer, wmsVersion, workspace } = currentConfig as any;

    const objectTypes = useMemo<ObjectTypeMeta[]>(() => {
        const stored = getItemWebStorage("objectTypes");
        return Array.isArray(stored) ? stored : [];
    }, []);

    const { queryType, captionSingular } = useMemo(() => {
        if (!objectTypeName) return { queryType: "objects", captionSingular: null as string | null };
        const meta = objectTypes.find((t) => t?.name === objectTypeName);
        return {
            queryType: meta?.queryType ?? "objects",
            captionSingular: meta?.captionSingular ?? null,
        };
    }, [objectTypes, objectTypeName]);

    const { data: objectInfo } = useGetTableObjectInfoQuery(
        { queryType, objectType: objectTypeName, objectId },
        { skip: !objectTypeName || !objectId }
    );

    const formattedData = useMemo(() => (objectInfo ? formatObject(objectInfo) : null), [objectInfo]);

    const mapTitle = useMemo(() => {
        return buildObjectCardHeaderTitle({
            objectTypeName,
            objectTypeCaptionSingular: captionSingular,
            formattedObjectInfo: formattedData,
            configByType: PROPERTIES_FOR_OBJECT_MAP_HEADER_LABEL,
        });
    }, [objectTypeName, captionSingular, formattedData]);

    const [geometryWkt, setGeometryWkt] = useState<string | string[] | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);

    useEffect(() => {
        if (!objectTypeName || !objectId) {
            setGeometryWkt(null);
            setIsLoading(false);
            setIsError(false);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        setIsLoading(true);
        setIsError(false);

        fetchObjectGeometry(queryType, objectTypeName, objectId, { signal: controller.signal })
            .then(({ data, isError, isSuccess }) => {
                if (cancelled) return;

                if (isSuccess && data) {
                    setGeometryWkt(data);
                    setIsError(false);
                } else {
                    setGeometryWkt(null);
                    setIsError(true);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                if (cancelled) return;
                // отмена запроса (AbortController) — не ошибка
                if ((error as any)?.name === "AbortError") return;
                setGeometryWkt(null);
                setIsError(true);
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [queryType, objectTypeName, objectId]);

    useEffect(() => {
        if (isError) {
            dispatch(
                showToastMessage({
                    messageText: "Не удалось загрузить объект на карте",
                    messageType: "error",
                })
            );
        }
    }, [isError, dispatch]);

    const objectTypeLayer = useMemo(() => {
        if (!objectTypeName) return null;

        if (objectTypeName === "AllObjects") {
            return getAllObjectsLayer();
        }

        const meta = objectTypes.find((t) => t?.name === objectTypeName);
        const mapLayer = meta?.mapLayer;

        if (!mapLayer || !wmsServer || !wmsVersion || !workspace) return null;

        const mainLayers = mapLayer.replace("MA", "MC");
        const layerFilter = (objectTypesDefaultFilters as any)?.[objectTypeName] ?? null;

        return getBasicObjectTypeLayer(mainLayers, wmsServer, wmsVersion, workspace, layerFilter);
    }, [objectTypeName, objectTypes, wmsServer, wmsVersion, workspace]);

    const geometryLayer = useMemo(() => {
        if (!geometryWkt) return null;
        return getWKTFeatureVectorLayer(geometryWkt);
    }, [geometryWkt]);

    const additionalLayers = useMemo(() => {
        return [objectTypeLayer, geometryLayer].filter(Boolean);
    }, [objectTypeLayer, geometryLayer]);

    const fitExtent = useMemo(() => {
        if (!geometryWkt) return undefined;
        return countExtentFromWKTFeatures(geometryWkt);
    }, [geometryWkt]);

    const fitExtentPadding = useMemo(() => {
        if (!geometryWkt) return undefined;
        return [350, 350, 350, 350] as const;
    }, [geometryWkt]);

    const applyMapObjectsFind = useCallback(
        (map: any) => {
            if (!objectTypeName || !objectTypeLayer) return { off: () => {} };

            const clickFunc = (event: any) => getObjectsOnWMSLayer(event, objectTypeName, objectTypeLayer);
            map?.on("singleclick", clickFunc);

            return {
                off: () => map?.un("singleclick", clickFunc),
            };
        },
        [objectTypeName, objectTypeLayer]
    );

    const mapEventHandlers = useMemo(
        () => [
            {
                name: "objectsFind",
                func: applyMapObjectsFind,
            },
        ],
        [applyMapObjectsFind]
    );

    return (
        <>
            <ContentHeader title={mapTitle} objectTypeName={objectTypeName} type="map" option="object" />
            <div className="map-widget-with-toast">
                <ToastMessageContainer />
                <LoaderWrapper isLoading={isLoading}>
                    <MapWidget
                        additionalLayers={additionalLayers}
                        propsFitExtent={fitExtent}
                        propsFitExtentPadding={fitExtentPadding}
                        mapEventHandlers={mapEventHandlers}
                    />
                </LoaderWrapper>
            </div>
        </>
    );
}